import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShopifyProduct, fetchProducts } from "@/lib/shopify";
import { ProductCard } from "./ProductCard";
import { Loader2, Package, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import useEmblaCarousel from "embla-carousel-react";

// Sort products: those with images first, without images last
const sortProductsByImages = (products: ShopifyProduct[]): ShopifyProduct[] => {
  return [...products].sort((a, b) => {
    const aHasImages = a.node.images.edges.length > 0;
    const bHasImages = b.node.images.edges.length > 0;
    if (aHasImages && !bHasImages) return -1;
    if (!aHasImages && bHasImages) return 1;
    return 0;
  });
};

// Filter products that are on sale (have compareAtPrice)
const filterSaleProducts = (products: ShopifyProduct[]): ShopifyProduct[] => {
  return products.filter((product) => {
    const variants = product.node.variants?.edges || [];
    return variants.some((v) => {
      const compareAt = v.node.compareAtPrice;
      if (!compareAt) return false;
      const compareAmount = parseFloat(compareAt.amount);
      const priceAmount = parseFloat(v.node.price.amount);
      return compareAmount > priceAmount;
    });
  });
};

// Featured products = products tagged with "featured" in Shopify
const filterForFeatured = (products: ShopifyProduct[]): ShopifyProduct[] => {
  return products.filter((product) => {
    const tags = (product.node.tags || []).map((t) => t.toLowerCase());
    return tags.includes("featured");
  });
};

export const ProductGrid = () => {
  const { t, getLocalizedPath } = useLanguage();
  const [saleMode, setSaleMode] = useState(false);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async (isInitial: boolean) => {
      if (isInitial) setLoading(true);

      // Sale section is shown whenever there is at least one Sale-tagged product.
      const saleData = await fetchProducts(250, 'tag:"Sale"');
      if (cancelled) return;

      const hasSale = saleData.length > 0;
      setSaleMode(hasSale);

      let filtered: ShopifyProduct[];
      if (hasSale) {
        filtered = saleData;
      } else {
        const data = await fetchProducts(350);
        if (cancelled) return;
        filtered = filterForFeatured(data);
        // If not enough featured products, fill with other products
        if (filtered.length < 12) {
          const featuredIds = new Set(filtered.map(p => p.node.id));
          const remaining = data.filter(p => !featuredIds.has(p.node.id));
          const sorted = sortProductsByImages(remaining);
          filtered = [...filtered, ...sorted.slice(0, 12 - filtered.length)];
        }
      }
      
      const sorted = sortProductsByImages(filtered);
      setProducts(sorted.slice(0, 12));

      if (isInitial) setLoading(false);
    };

    loadProducts(true);

    const interval = window.setInterval(() => {
      loadProducts(false);
    }, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  // Section title & link based on mode
  const sectionLabel = saleMode ? t.nav.sale : t.shop.collection;
  const sectionTitle = saleMode
    ? (t.nav.sale + " — " + t.product.featuredProducts)
    : t.product.featuredProducts;
  const ctaLink = saleMode
    ? getLocalizedPath("/shop?tag=Sale")
    : getLocalizedPath("/shop");
  const ctaText = saleMode
    ? t.nav.sale
    : t.product.shopFullCollection;

  if (loading) {
    return (
      <section id="products" className="py-24 lg:py-32 bg-charcoal">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gold mx-auto" />
            <p className="text-cream/50 mt-4 text-sm font-body">{t.common.loading}</p>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section id="products" className="py-24 lg:py-32 bg-charcoal">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <span className="text-gold font-semibold text-xs tracking-widest uppercase mb-3 block">
              {sectionLabel}
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-cream">
              {saleMode ? t.nav.sale : t.product.featuredProducts}
            </h2>
          </div>

          <div className="max-w-sm mx-auto text-center py-12 bg-charcoal-light rounded-2xl border border-cream/10">
            <div className="w-16 h-16 rounded-2xl bg-cream/5 flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-cream/30" />
            </div>
            <h3 className="font-display text-lg font-semibold text-cream mb-2">{t.product.noProducts}</h3>
            <p className="text-cream/50 text-sm font-body">
              {t.product.noProductsDesc}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="py-24 lg:py-32 bg-charcoal">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-cream font-display text-2xl md:text-3xl tracking-wide">
            {saleMode ? t.nav.sale : t.product.featuredProducts}
          </h2>
        </div>
        <div className="flex justify-end gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={scrollPrev}
                className="border-cream/20 text-cream hover:bg-cream/10 hover:text-gold"
                aria-label="Previous products"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={scrollNext}
                className="border-cream/20 text-cream hover:bg-cream/10 hover:text-gold"
                aria-label="Next products"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            <Button variant="ghost" size="lg" asChild className="text-cream/60 hover:text-gold hidden lg:flex">
              <Link to={ctaLink} className="gap-2">
                {t.product.viewAll}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Products carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4 lg:gap-6">
            {products.map((product, index) => (
              <div 
                key={product.node.id} 
                className="flex-none w-[280px] sm:w-[300px] lg:w-[320px]"
              >
                <ProductCard product={product} index={index} />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button variant="hero" size="xl" asChild>
            <Link to={ctaLink} className="gap-2">
              {ctaText}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
